import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaArrowLeft } from 'react-icons/fa';
import Header from './components/Header';
import { useLanguage } from './i18n';

const PrivacyPolicyPage = () => {
  const { t, tArray } = useLanguage();

  useEffect(() => {
    document.title = t('privacy.seo.title');
    window.scrollTo(0, 0);
  }, [t]);

  const sections = tArray('privacy.sections');

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl mb-6 shadow-lg shadow-orange-500/30">
              <FaShieldAlt className="text-4xl text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('privacy.title')}
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              {t('privacy.subtitle')}
            </p>
            <p className="text-gray-500">
              {t('privacy.lastUpdated')}: {t('privacy.updateDate')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Link */}
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 mb-8 transition-colors"
            >
              <FaArrowLeft />
              {t('privacy.backHome')}
            </Link>

            {/* Policy Content */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 p-8 md:p-12">
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-gray-300 leading-relaxed">
                  {t('privacy.introduction')}
                </p>
              </div>

              {/* Sections */}
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="mb-10"
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                    <span className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    {section.title}
                  </h2>
                  <div className="pl-11">
                    <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </p>
                    {section.items && (
                      <ul className="mt-4 space-y-2">
                        {section.items.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-400">
                            <span className="text-orange-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Contact Section */}
              <div className="mt-12 pt-8 border-t border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('privacy.contact.title')}
                </h2>
                <p className="text-gray-300 mb-4">
                  {t('privacy.contact.description')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-orange-500/30 transition-all"
                  >
                    {t('privacy.contact.button')}
                  </Link>
                  <Link
                    to="/cookie-policy"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-600 transition-all"
                  >
                    {t('privacy.contact.cookiePolicy')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-800">
        <div className="container mx-auto px-4 text-center text-gray-500">
          <p>&copy; {new Date().getFullYear()} Tunnel Coaching 1-1. {t('footer.designedBy')} Glotone</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
